import {isPlatformBrowser} from '@angular/common';
import {Inject, Injectable, OnDestroy, PLATFORM_ID} from '@angular/core';
import {Subject} from 'rxjs';
import {TokenDataProviderService} from '../../core/services/token-data-provider.service';

export const CHAT_CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export function chatConversationKey(iri: string): string {
  return iri ? `/conversations/${iri.split('/').filter(Boolean).pop()}` : '';
}

interface CacheMetadata {
  key: string;
  kind: 'snapshot' | 'image';
  resource: string;
  conversation?: string;
  size: number;
  expires: number;
  accessed: number;
}

interface CacheOwner {user: string; epoch: string;}

/** Private, bounded storage. All writes are fenced by a durable login epoch. */
@Injectable({providedIn: 'root'})
export class ChatCacheStoreService implements OnDestroy {
  private readonly dbName = 'wtg-chat-cache-v1';
  private readonly limits = {snapshot: 32 * 1024 * 1024, image: 256 * 1024 * 1024};
  private readonly signalKey = 'wtg.chat-cache.reset';
  private readonly browser: boolean;
  private dbPromise?: Promise<IDBDatabase | null>;
  private readyPromise?: Promise<void>;
  private owner: CacheOwner;
  private generation = 0;
  private destroyed = false;
  private blockedOwner: string | null = null;
  private channel?: BroadcastChannel;
  private readonly seenResets = new Set<string>();
  private readonly revokedConversations = new Set<string>();
  private readonly resetSource = new Subject<void>();
  private readonly revokedSource = new Subject<string>();
  readonly reset$ = this.resetSource.asObservable();
  readonly conversationRevoked$ = this.revokedSource.asObservable();
  private readonly sessionSubscription;

  constructor(private readonly session: TokenDataProviderService, @Inject(PLATFORM_ID) platformId: object) {
    this.browser = isPlatformBrowser(platformId);
    this.owner = {user: this.readUser(), epoch: this.newEpoch()};
    this.sessionSubscription = session.sessionChanged$.subscribe(() => {
      const user = this.readUser();
      if (user !== this.owner.user || !user || this.blockedOwner !== null) this.reset(user);
    });
    if (this.browser) {
      if (typeof BroadcastChannel !== 'undefined') {
        try {
          this.channel = new BroadcastChannel(this.signalKey);
          this.channel.onmessage = event => this.receiveReset(event.data);
        } catch { /* storage events remain available when channels are disabled. */ }
      }
      window.addEventListener('storage', this.onStorage);
    }
  }

  get userKey(): string {
    const user = this.readUser();
    if (this.blockedOwner !== null) {
      if (user !== this.blockedOwner) return '';
      this.blockedOwner = null;
    }
    if (user !== this.owner.user) this.reset(user);
    return this.browser ? user : '';
  }

  get epoch(): number { return this.generation; }

  async snapshots(): Promise<Array<{resource: string; value: any}>> {
    const result: Array<{resource: string; value: any}> = [];
    const context = await this.context();
    if (!context) return result;
    try {
      const rows = await this.metadata(context.db);
      for (const row of rows.filter(item => item.kind === 'snapshot')) {
        const value = await this.read(row.resource, 'snapshot');
        if (value !== null && context.generation === this.generation) result.push({resource: row.resource, value});
      }
    } catch { /* Storage is an optimisation, never a prerequisite for chat. */ }
    return context.generation === this.generation ? result : [];
  }

  snapshot<T>(resource: string): Promise<T | null> { return this.read(resource, 'snapshot'); }

  saveSnapshot(resource: string, value: unknown, syncedAt: number, conversation?: string): Promise<void> {
    try {
      const json = JSON.stringify(value);
      return this.write(resource, 'snapshot', JSON.parse(json), new Blob([json]).size, syncedAt, conversation);
    } catch { return Promise.resolve(); }
  }

  image(url: string, version: string): Promise<{blob: Blob; expires: number} | null> { return this.read(this.imageKey(url, version), 'image', true); }

  saveImage(url: string, version: string, blob: Blob, conversation?: string): Promise<void> {
    return this.write(this.imageKey(url, version), 'image', blob, blob.size, Date.now(), conversation);
  }

  deleteImage(url: string, version: string): Promise<void> { return this.remove(this.imageKey(url, version), 'image'); }

  async forgetConversation(conversation: string): Promise<void> {
    conversation = chatConversationKey(conversation);
    this.revokedConversations.add(conversation);
    this.revokedSource.next(conversation);
    const context = await this.context();
    if (!context) return;
    await this.mutate(context, tx => {
      const request = tx.objectStore('metadata').getAll();
      request.onsuccess = () => (request.result as CacheMetadata[]).forEach(row => {
        if (row.conversation === conversation) {
          tx.objectStore('metadata').delete(row.key);
          tx.objectStore('payloads').delete(row.key);
        }
      });
    });
  }

  reset(user = this.readUser()): void {
    this.blockedOwner = null;
    const owner = {user, epoch: this.newEpoch()};
    this.applyReset(owner);
    // Publish after the disk fence commits. Receivers never overwrite a newer
    // fence with a delayed BroadcastChannel/storage event from an older login.
    void this.readyPromise.then(() => { if (this.owner.epoch === owner.epoch) this.publishReset(owner); });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.sessionSubscription.unsubscribe();
    this.channel?.close();
    if (this.browser) window.removeEventListener('storage', this.onStorage);
    void this.dbPromise?.then(db => db?.close());
  }

  private readUser(): string {
    if (!this.browser) return '';
    const user = this.session.getJwtUser();
    return String(user?.id || user?.['@id']?.split('/').filter(Boolean).pop() || '');
  }

  private newEpoch(): string { return `${Date.now()}-${Math.random()}`; }

  private readonly onStorage = (event: StorageEvent): void => {
    if (event.key !== this.signalKey || !event.newValue) return;
    try { this.receiveReset(JSON.parse(event.newValue)); } catch { /* Invalid signal. */ }
  };

  private receiveReset(owner: CacheOwner): void {
    if (this.destroyed || !owner || typeof owner.user !== 'string' || typeof owner.epoch !== 'string' || this.seenResets.has(owner.epoch)) return;
    void this.open().then(db => {
      const accept = () => {
        if (this.destroyed || this.seenResets.has(owner.epoch)) return;
        this.blockedOwner = this.readUser() === owner.user ? null : owner.user;
        this.applyReset(owner, false);
      };
      if (!db) { accept(); return; }
      try {
        const request = db.transaction('control').objectStore('control').get('owner');
        request.onsuccess = () => { if (request.result?.epoch === owner.epoch) accept(); };
      } catch { accept(); }
    });
  }

  private publishReset(owner: CacheOwner): void {
    if (this.destroyed) return;
    this.channel?.postMessage(owner);
    try { if (this.browser) localStorage.setItem(this.signalKey, JSON.stringify(owner)); } catch { /* Disabled storage. */ }
  }

  private applyReset(owner: CacheOwner, clearDisk = true): void {
    this.seenResets.add(owner.epoch);
    this.generation++;
    this.revokedConversations.clear();
    this.owner = owner;
    // Start the clearing transaction before publishing the reset to consumers.
    this.readyPromise = clearDisk ? this.open().then(db => db ? this.initializeOwner(db, owner, true) : undefined) : Promise.resolve();
    this.resetSource.next();
  }

  private async context(): Promise<{db: IDBDatabase; generation: number; owner: CacheOwner} | null> {
    if (!this.userKey) return null;
    const generation = this.generation;
    const db = await this.open();
    if (!db) return null;
    if (!this.readyPromise) this.readyPromise = this.initializeOwner(db, this.owner, false);
    await this.readyPromise;
    return generation === this.generation && this.userKey ? {db, generation, owner: {...this.owner}} : null;
  }

  private open(): Promise<IDBDatabase | null> {
    if (this.dbPromise) return this.dbPromise;
    if (!this.browser || typeof indexedDB === 'undefined') return Promise.resolve(null);
    this.dbPromise = new Promise(resolve => {
      let expired = false;
      const timer = setTimeout(() => { expired = true; resolve(null); }, 1500);
      try {
        const request = indexedDB.open(this.dbName, 1);
        request.onupgradeneeded = () => {
          for (const name of ['payloads', 'metadata', 'control']) request.result.createObjectStore(name);
        };
        request.onsuccess = () => {
          clearTimeout(timer);
          if (expired) { request.result.close(); return; }
          request.result.onversionchange = () => request.result.close();
          resolve(request.result);
        };
        request.onerror = () => { clearTimeout(timer); resolve(null); };
      } catch { clearTimeout(timer); resolve(null); }
    });
    return this.dbPromise;
  }

  private initializeOwner(db: IDBDatabase, owner: CacheOwner, clear: boolean): Promise<void> {
    return new Promise(resolve => {
      try {
        let changedAccount = false;
        const tx = db.transaction(['payloads', 'metadata', 'control'], 'readwrite');
        const control = tx.objectStore('control');
        const request = control.get('owner');
        request.onsuccess = () => {
          const previous: CacheOwner | undefined = request.result;
          changedAccount = !!previous && previous.user !== owner.user;
          if (clear || previous?.user !== owner.user) {
            tx.objectStore('payloads').clear();
            tx.objectStore('metadata').clear();
            control.put(owner, 'owner');
          } else if (this.owner === owner) {
            this.owner = previous;
          }
        };
        tx.oncomplete = () => {
          if (changedAccount && !clear && this.owner.epoch === owner.epoch) this.publishReset(owner);
          resolve();
        };
        tx.onabort = tx.onerror = () => resolve();
      } catch { resolve(); }
    });
  }

  private async read<T>(resource: string, kind: CacheMetadata['kind'], includeExpiry = false): Promise<T | null> {
    const context = await this.context();
    if (!context) return null;
    return new Promise(resolve => {
      let value: T | null = null;
      try {
        const tx = context.db.transaction(['payloads', 'metadata', 'control'], 'readwrite');
        const key = JSON.stringify([context.owner.user, kind, resource]);
        const owner = tx.objectStore('control').get('owner');
        owner.onsuccess = () => {
          if (owner.result?.epoch !== context.owner.epoch) return;
          const metadata = tx.objectStore('metadata');
          const request = metadata.get(key);
          request.onsuccess = () => {
            const row: CacheMetadata | undefined = request.result;
            if (!row || !Number.isFinite(row.expires) || row.expires <= Date.now()) {
              metadata.delete(key);
              tx.objectStore('payloads').delete(key);
              return;
            }
            metadata.put({...row, accessed: Date.now()}, key);
            const payload = tx.objectStore('payloads').get(key);
            payload.onsuccess = () => { value = payload.result == null ? null : (includeExpiry ? {blob: payload.result, expires: row.expires} : payload.result); };
          };
        };
        tx.oncomplete = () => resolve(context.generation === this.generation ? value : null);
        tx.onabort = tx.onerror = () => resolve(null);
      } catch { resolve(null); }
    });
  }

  private async write(resource: string, kind: CacheMetadata['kind'], value: unknown, size: number, syncedAt: number, conversation?: string): Promise<void> {
    conversation = chatConversationKey(conversation);
    const generation = this.generation;
    if (!syncedAt || syncedAt + CHAT_CACHE_TTL_MS <= Date.now() || size > this.limits[kind]) return;
    const context = await this.context();
    if (!context || generation !== context.generation) return;
    await this.mutate(context, tx => {
      if (conversation && this.revokedConversations.has(conversation)) return;
      const key = JSON.stringify([context.owner.user, kind, resource]);
      const metadata = tx.objectStore('metadata');
      metadata.put({key, kind, resource, conversation, size, expires: syncedAt + CHAT_CACHE_TTL_MS, accessed: Date.now()} as CacheMetadata, key);
      tx.objectStore('payloads').put(value, key);
      const request = metadata.getAll();
      request.onsuccess = () => {
        const rows = (request.result as CacheMetadata[]).sort((a, b) => a.accessed - b.accessed);
        let bytes = rows.filter(row => row.kind === kind).reduce((sum, row) => sum + row.size, 0);
        for (const row of rows) {
          if (row.expires <= Date.now() || (row.kind === kind && bytes > this.limits[kind])) {
            metadata.delete(row.key);
            tx.objectStore('payloads').delete(row.key);
            if (row.kind === kind) bytes -= row.size;
          }
        }
      };
    });
  }

  private async remove(resource: string, kind: CacheMetadata['kind']): Promise<void> {
    const context = await this.context();
    if (!context) return;
    await this.mutate(context, tx => {
      const key = JSON.stringify([context.owner.user, kind, resource]);
      tx.objectStore('metadata').delete(key);
      tx.objectStore('payloads').delete(key);
    });
  }

  private mutate(context: {db: IDBDatabase; generation: number; owner: CacheOwner}, work: (tx: IDBTransaction) => void): Promise<void> {
    return new Promise(resolve => {
      try {
        const tx = context.db.transaction(['payloads', 'metadata', 'control'], 'readwrite');
        const owner = tx.objectStore('control').get('owner');
        owner.onsuccess = () => {
          if (context.generation === this.generation && owner.result?.epoch === context.owner.epoch) {
            try { work(tx); } catch { tx.abort(); }
          }
        };
        tx.oncomplete = () => resolve();
        // Quota and storage failures must not break the RAM/network path.
        tx.onabort = tx.onerror = () => resolve();
      } catch { resolve(); }
    });
  }

  private metadata(db: IDBDatabase): Promise<CacheMetadata[]> {
    return new Promise((resolve, reject) => {
      const request = db.transaction('metadata').objectStore('metadata').getAll();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  private imageKey(url: string, version: string): string { return JSON.stringify([url, version]); }
}
